import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconBack } from '../components/Icon';
import { useScheduleModel } from '../lib/schedule';
import { durationLabel, fmtDay, fmtTime } from '../lib/time';
import ResortPlan from '../components/ResortPlan';
import NotFound from './NotFound';

/**
 * Artboard 1e — one event, in full.
 * The dress code is the reason this page exists. It gets the prose version,
 * with the tags underneath; the schedule row upstairs carries only the short
 * form (D41).
 */
export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const model = useScheduleModel();

  if (model.loading) {
    return <div className="page page-pad"><div className="sk sk-block" style={{ height: 260, marginTop: 20 }} /></div>;
  }

  const event = model.events.find((e) => e.id === id);
  if (!event) return <NotFound />;

  const venue = model.venueOf(event.venueId);
  const duration = durationLabel(event.start, event.end);

  return (
    <div className="page page-narrow" style={{ padding: '8px 20px 96px' }}>
      <button type="button" className="btn btn-secondary" style={{ minHeight: 42, gap: 8 }} onClick={() => navigate(-1)}>
        <IconBack size={16} /> Schedule
      </button>

      <div className="kicker" style={{ marginTop: 16 }}>
        {fmtDay(event.start)} · {fmtTime(event.start)}{event.end ? `–${fmtTime(event.end)}` : ''}
        {duration ? ` · ${duration}` : ''}
      </div>
      <h2 style={{ margin: '4px 0 10px', fontSize: 32 }}>{event.name}</h2>

      {venue && (
        <div className="card" style={{ flexDirection: 'row', alignItems: 'center', gap: 14, padding: 12, background: 'var(--color-surface)' }}>
          <Link to={`/map/${venue.id}`} style={{ flex: 'none' }}>
            <span className="venue-thumb">
              <ResortPlan className="venue-thumb-plan" focus={{ x: venue.x, y: venue.y }} label={`${venue.name} on the resort plan`} />
            </span>
          </Link>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16 }}>{venue.name}</div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>{venue.where}</div>
          </div>
          <a
            className="btn btn-primary" style={{ minHeight: 40, paddingInline: 16, flex: 'none' }}
            href={venue.navUrl} target="_blank" rel="noreferrer noopener"
          >
            Navigate
          </a>
        </div>
      )}

      {event.dressCode && (
        <div className="card dress" style={{ marginTop: 16 }}>
          <div className="dress-kicker">Dress code</div>
          <div className="dress-head">{event.dressCode.short}</div>
          {event.dressCode.full && <p>{event.dressCode.full}</p>}
          {(event.dressCode.men || event.dressCode.women) && (
            <dl className="dress-split">
              {event.dressCode.men && <><dt>Men</dt><dd>{event.dressCode.men}</dd></>}
              {event.dressCode.women && <><dt>Women</dt><dd>{event.dressCode.women}</dd></>}
            </dl>
          )}
          {event.dressCode.tags?.length && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              {event.dressCode.tags.map((t) => <span key={t} className="tag tag-outline">{t}</span>)}
            </div>
          )}
        </div>
      )}

      {event.notes && (
        <div style={{ marginTop: 14 }}>
          <h5 style={{ margin: '0 0 5px' }}>Notes from us</h5>
          <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>{event.notes}</p>
        </div>
      )}

      {model.wardrobe?.pdfUrl && (
        <p style={{ fontSize: 12, opacity: 0.55, marginTop: 18 }}>
          Packing for the whole week?{' '}
          <a href={model.wardrobe.pdfUrl} target="_blank" rel="noreferrer noopener">The wardrobe planner</a> covers every event.
        </p>
      )}
    </div>
  );
}
